import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { fetchContractsCoverage } from '../../../Redux/Actions/actions';
import { FaFileSignature, FaExclamationTriangle, FaHome } from 'react-icons/fa';
import { RENTAL_TYPE, formatDate, getDetailPath } from '../utils/rentalTypeHelpers';
import style from './ContractsCoverage.module.css';

const ContractsCoverage = () => {
  const dispatch = useDispatch();
  const coverage = useSelector((state) => state.contractsCoverage);
  const auditLoading = useSelector((state) => state.auditLoading);
  const [rentalType, setRentalType] = useState(RENTAL_TYPE.REGULAR);

  useEffect(() => {
    dispatch(fetchContractsCoverage(rentalType)).catch(() => {});
  }, [dispatch, rentalType]);

  const breakdown = coverage?.breakdown || {};
  const pct = Number(coverage?.coveragePercent || 0);
  const missing = coverage?.missingContract || [];

  const breakdownCards = [
    { key: 'signedAndDocumented', label: 'Firmado y documentado', color: '#10b981' },
    { key: 'signedNoDoc', label: 'Firmado sin documento', color: '#f59e0b' },
    { key: 'documentedNotSigned', label: 'Documentado sin firmar', color: '#3b82f6' },
    { key: 'noContract', label: 'Sin contrato', color: '#ef4444' },
  ];

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaFileSignature className={style.titleIcon} />
          Cobertura de Contratos
        </h1>
        <p className={style.subtitle}>
          Qué contratos están firmados y documentados, y cuáles están pendientes.
        </p>
      </div>

      <div className={style.typeTabs}>
        {[
          { v: RENTAL_TYPE.REGULAR, l: 'Alquileres completos' },
          { v: RENTAL_TYPE.ADMIN, l: 'Solo administración' },
        ].map((opt) => (
          <button
            key={opt.v}
            type="button"
            className={`${style.typeTab} ${rentalType === opt.v ? style.typeTabActive : ''}`}
            onClick={() => setRentalType(opt.v)}
          >
            {opt.l}
          </button>
        ))}
      </div>

      {auditLoading && !coverage ? (
        <div className={style.stateBox}>Cargando cobertura...</div>
      ) : !coverage ? (
        <div className={style.stateBox}>No hay datos de cobertura disponibles.</div>
      ) : (
        <>
          <div className={style.coverageHero}>
            <div className={style.coverageMain}>
              <span className={style.coveragePercent}>{pct.toFixed(1)}%</span>
              <span className={style.coverageLabel}>
                {coverage.covered} de {coverage.total} contratos con contrato
              </span>
            </div>
            <div className={style.progressTrack}>
              <div
                className={style.progressFill}
                style={{ width: `${Math.min(100, Math.max(0, pct))}%` }}
              />
            </div>
          </div>

          <div className={style.breakdownGrid}>
            {breakdownCards.map((c) => (
              <div key={c.key} className={style.breakdownCard}>
                <span className={style.breakdownDot} style={{ background: c.color }} />
                <span className={style.breakdownValue}>{breakdown[c.key] ?? 0}</span>
                <span className={style.breakdownLabel}>{c.label}</span>
              </div>
            ))}
          </div>

          <div className={style.missingSection}>
            <h2 className={style.sectionTitle}>
              <FaExclamationTriangle style={{ color: '#ef4444' }} />
              Pendientes de documentar/firmar ({missing.length})
            </h2>
            {missing.length === 0 ? (
              <div className={style.stateBox}>
                Todos los contratos están firmados y documentados. 🎉
              </div>
            ) : (
              <div className={style.missingList}>
                {missing.map((m) => (
                  <Link
                    key={m.id}
                    to={getDetailPath(m.id, rentalType)}
                    className={style.missingCard}
                  >
                    <div className={style.missingIcon}>
                      <FaHome />
                    </div>
                    <div className={style.missingBody}>
                      <span className={style.missingAddress}>
                        {m.property?.address || 'Dirección no disponible'}
                      </span>
                      <span className={style.missingMeta}>
                        {m.property?.neighborhood ? `${m.property.neighborhood} · ` : ''}
                        {m.property?.propertyType || ''}
                      </span>
                      <span className={style.missingMeta}>
                        Propietario: {m.ownerName || '—'}
                      </span>
                      <span className={style.missingDates}>
                        {formatDate(m.contractStart)} → {formatDate(m.contractEnd)}
                      </span>
                    </div>
                    <span className={style.missingStatus}>{m.contractStatus}</span>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ContractsCoverage;
